import React, { useState, useEffect } from 'react';
import { workflowTemplatesApi } from '../../api/admin';
import toast from 'react-hot-toast';
import {
  X, Check, Package, ChevronRight, AlertTriangle, Clock,
  GitBranch, ListChecks, Layout, Database, ArrowRight,
  Heart, Users, Briefcase, Globe, FileText
} from 'lucide-react';

const ICON_MAP = {
  car: Briefcase, gavel: Briefcase, heart: Heart, users: Users,
  briefcase: Briefcase, globe: Globe, workflow: GitBranch,
  'file-plus': FileText, folder: Package,
};

const PRACTICE_LABELS = {
  personal_injury: 'Personal Injury',
  family_law: 'Family Law',
  immigration: 'Immigration',
  real_estate: 'Real Estate',
  general: 'General',
};

export default function WorkflowTemplateStore({ recordTypeId, onInstalled, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState('browse'); // browse | preview | conflicts | installing
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [fieldResolutions, setFieldResolutions] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const data = await workflowTemplatesApi.list(filter === 'all' ? undefined : filter);
      setTemplates(data.templates);
    } catch (err) {
      toast.error('Failed to load workflow templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, [filter]);

  const [premiumModal, setPremiumModal] = useState(null);

  const handleSelect = async (tpl) => {
    // Show premium notice for paid templates
    if (tpl.price_type === 'premium' && tpl.price_cents > 0) {
      setPremiumModal(tpl);
      return;
    }
    setSelected(tpl);
    setPreviewing(true);
    setStep('preview');
    try {
      const data = await workflowTemplatesApi.preview(tpl.id, recordTypeId);
      setPreview(data);
      if (data.conflicts.length > 0) {
        const defaults = {};
        data.conflicts.forEach(c => { defaults[c.api_name] = 'use_existing'; });
        setFieldResolutions(defaults);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to preview');
      setStep('browse');
      setSelected(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleInstall = async () => {
    setInstalling(true);
    setStep('installing');
    try {
      const data = await workflowTemplatesApi.install(selected.id, recordTypeId, fieldResolutions);
      setResult(data);
      setStep('done');
      toast.success(`${data.template_name} installed successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Installation failed');
      setStep('preview');
    } finally {
      setInstalling(false);
    }
  };

  const practices = ['all', ...new Set(templates.map(t => t.practice_area))];
  const filtered = filter === 'all' ? templates : templates.filter(t => t.practice_area === filter);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-brand-50 flex items-center justify-center">
              <Package className="h-4.5 w-4.5 text-brand-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                {step === 'browse' ? 'Workflow Templates' :
                 step === 'done' ? 'Installation Complete' :
                 selected?.name}
              </h2>
              <p className="text-xs text-gray-400">
                {step === 'browse' ? 'Add a pre-built workflow to this record type' :
                 step === 'preview' ? 'Review what will be installed' :
                 step === 'conflicts' ? 'Resolve field conflicts' :
                 step === 'installing' ? 'Installing...' :
                 'Workflow has been added'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {step === 'browse' && (
            <BrowseStep
              templates={filtered}
              loading={loading}
              filter={filter}
              practices={practices}
              onFilter={setFilter}
              onSelect={handleSelect}
            />
          )}
          {step === 'preview' && (
            <PreviewStep
              template={selected}
              preview={preview}
              previewing={previewing}
              fieldResolutions={fieldResolutions}
              onResolutionChange={(name, val) => setFieldResolutions(prev => ({ ...prev, [name]: val }))}
              onInstall={handleInstall}
              onBack={() => { setStep('browse'); setSelected(null); setPreview(null); }}
            />
          )}
          {step === 'installing' && (
            <div className="flex flex-col items-center justify-center py-16">
              <Clock className="h-10 w-10 text-brand-600 animate-spin mb-4" />
              <p className="text-sm text-gray-500">Installing {selected?.name}...</p>
              <p className="text-xs text-gray-400 mt-1">Creating stages, tasks, screens, and fields</p>
            </div>
          )}
          {step === 'done' && result && (
            <DoneStep result={result} onClose={() => { onClose(); onInstalled && onInstalled(); }} />
          )}
        </div>

        {/* Premium template modal */}
        {premiumModal && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl" onClick={() => setPremiumModal(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()}>
              <div className="text-center mb-4">
                <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-2">
                  ${((premiumModal.price_cents || 0) / 100).toFixed(0)} {premiumModal.price_description || ''}
                </span>
                <h3 className="text-lg font-semibold text-gray-900">{premiumModal.name}</h3>
              </div>
              <p className="text-sm text-gray-500 text-center mb-4">
                This is a premium template. Paid template purchasing is coming soon. Contact support to get access.
              </p>
              <button onClick={() => setPremiumModal(null)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BrowseStep({ templates, loading, filter, practices, onFilter, onSelect }) {
  if (loading) return <div className="py-16 text-center text-gray-400">Loading templates...</div>;

  return (
    <div className="p-6">
      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {practices.map(p => (
          <button key={p} onClick={() => onFilter(p)}
            className={'px-3 py-1.5 text-xs font-medium rounded-full transition-colors ' +
              (filter === p ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
            {p === 'all' ? 'All' : PRACTICE_LABELS[p] || p}
          </button>
        ))}
      </div>

      {/* Template cards */}
      <div className="space-y-3">
        {templates.map(tpl => {
          const Icon = ICON_MAP[tpl.icon] || GitBranch;
          const features = typeof tpl.features === 'string' ? JSON.parse(tpl.features) : (tpl.features || []);
          return (
            <div key={tpl.id}
              onClick={() => onSelect(tpl)}
              className="bg-white border border-gray-200 rounded-xl p-4 hover:border-brand-300 hover:shadow-md cursor-pointer transition-all group">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: tpl.color + '15' }}>
                  <Icon className="h-5 w-5" style={{ color: tpl.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-gray-900">{tpl.name}</h3>
                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand-500 transition-colors" />
                  </div>
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2">{tpl.description}</p>
                  <div className="flex items-center gap-3 text-[11px] text-gray-400">
                    <span className="flex items-center gap-1">
                      <GitBranch className="h-3 w-3" /> {tpl.stage_count} stages
                    </span>
                    <span className="flex items-center gap-1">
                      <ListChecks className="h-3 w-3" /> {tpl.task_count} tasks
                    </span>
                    {Number(tpl.screen_count) > 0 && (
                      <span className="flex items-center gap-1">
                        <Layout className="h-3 w-3" /> {tpl.screen_count} screens
                      </span>
                    )}
                    {Number(tpl.field_count) > 0 && (
                      <span className="flex items-center gap-1">
                        <Database className="h-3 w-3" /> {tpl.field_count} fields
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-1.5">
                      {tpl.is_lite && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-medium">Lite</span>}
                      {tpl.price_type === 'premium' ? (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          ${((tpl.price_cents || 0) / 100).toFixed(0)}{tpl.price_interval === 'annual' ? '/yr' : ''}
                        </span>
                      ) : tpl.price_type === 'included' ? (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Starter+</span>
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-600">Free</span>
                      )}
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: tpl.color + '15', color: tpl.color }}>
                        {PRACTICE_LABELS[tpl.practice_area] || tpl.practice_area}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {templates.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">No workflow templates available in this category.</div>
      )}
    </div>
  );
}

function PreviewStep({ template, preview, previewing, fieldResolutions, onResolutionChange, onInstall, onBack }) {
  if (previewing || !preview) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Clock className="h-8 w-8 text-brand-500 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Analyzing compatibility...</p>
      </div>
    );
  }

  const s = preview.summary;
  const hasConflicts = preview.conflicts.length > 0;

  return (
    <div className="p-6 space-y-5">
      {/* What will be created */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">What will be installed</h3>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Stages', count: s.stages, icon: GitBranch, color: '#6366F1' },
            { label: 'Transitions', count: s.transitions, icon: ArrowRight, color: '#8B5CF6' },
            { label: 'Tasks', count: s.tasks, icon: ListChecks, color: '#3B82F6' },
            { label: 'New Screens', count: s.new_screens, icon: Layout, color: '#14B8A6' },
            { label: 'New Fields', count: s.new_fields, icon: Database, color: '#F59E0B' },
            { label: 'Reused Fields', count: s.shared_fields, icon: Check, color: '#22C55E' },
          ].map(item => (
            <div key={item.label} className="bg-gray-50 rounded-lg p-3 text-center">
              <item.icon className="h-4 w-4 mx-auto mb-1" style={{ color: item.color }} />
              <div className="text-lg font-bold text-gray-900">{item.count}</div>
              <div className="text-[11px] text-gray-500">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Reused screens */}
      {preview.reused_screens.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Shared Screens (already exist)</h3>
          <div className="flex flex-wrap gap-1.5">
            {preview.reused_screens.map(s => (
              <span key={s.slug} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-md font-medium">
                {s.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* New screens */}
      {preview.new_screens.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">New Screens</h3>
          <div className="flex flex-wrap gap-1.5">
            {preview.new_screens.map(s => (
              <span key={s.slug} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md font-medium">
                {s.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Field conflicts */}
      {hasConflicts && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-amber-800">{preview.conflicts.length} Field Conflict{preview.conflicts.length > 1 ? 's' : ''}</h3>
          </div>
          <p className="text-xs text-amber-700 mb-3">
            These fields exist on your record type but have a different type than the template expects. Choose how to handle each:
          </p>
          <div className="space-y-3">
            {preview.conflicts.map(conflict => (
              <div key={conflict.api_name} className="bg-white rounded-lg p-3 border border-amber-100">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{conflict.template_label}</span>
                    <span className="text-xs text-gray-400 ml-2">({conflict.api_name})</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-mono">{conflict.existing_type}</span>
                  <ArrowRight className="h-3 w-3" />
                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-mono">{conflict.template_type}</span>
                </div>
                <div className="flex gap-2">
                  {[
                    { val: 'use_existing', label: 'Keep Existing', desc: 'Use current field as-is' },
                    { val: 'create_new', label: 'Create New', desc: 'Add with suffix' },
                    { val: 'skip', label: 'Skip', desc: 'Don\'t add this field' },
                  ].map(opt => (
                    <button key={opt.val}
                      onClick={() => onResolutionChange(conflict.api_name, opt.val)}
                      className={'flex-1 py-1.5 px-2 rounded-md text-xs font-medium border transition-colors ' +
                        (fieldResolutions[conflict.api_name] === opt.val
                          ? 'bg-brand-50 border-brand-300 text-brand-700'
                          : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50')}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack}
          className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
          Back
        </button>
        <button onClick={onInstall}
          className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 transition-colors flex items-center justify-center gap-2">
          <Package className="h-4 w-4" />
          Install {template.name}
        </button>
      </div>
    </div>
  );
}

function DoneStep({ result, onClose }) {
  const r = result.resources;
  return (
    <div className="p-6 text-center">
      <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
        <Check className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-1">{result.template_name} Installed!</h3>
      <p className="text-sm text-gray-500 mb-6">The workflow has been added to your record type.</p>

      <div className="grid grid-cols-3 gap-3 mb-6 max-w-sm mx-auto">
        {[
          { label: 'Stages', count: r.stages },
          { label: 'Tasks', count: r.tasks },
          { label: 'Transitions', count: r.transitions },
          { label: 'Screens', count: r.screens },
          { label: 'Fields', count: r.fields },
          { label: 'Screen Tabs', count: r.stage_screens },
        ].filter(i => i.count > 0).map(item => (
          <div key={item.label} className="bg-gray-50 rounded-lg p-2">
            <div className="text-base font-bold text-gray-900">{item.count}</div>
            <div className="text-[11px] text-gray-500">{item.label}</div>
          </div>
        ))}
      </div>

      <button onClick={onClose}
        className="w-full py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700">
        Done
      </button>
    </div>
  );
}
